import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';
import NotificationBell from '../NotificationBell';
import Avatar from '../ui/Avatar';
import { useScrollNavigation } from '../../hooks/useScrollNavigation';
import type { NavLinkItem } from './types';
import { Brand } from './Brand';

const MOBILE_MENU_ID = 'mobile-menu-drawer';

export interface MobileNavProps {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  links: NavLinkItem[];
  logoSrc: string;
  logoAlt: string;
  phoneNumber: string;
  location: { pathname: string; hash: string };
  onLoginClick: () => void;
  user: any;
  userRole: string | null;
  userProfile: any;
  hasQuotes: boolean;
  hasCarrier: boolean;
  onAvatarClick: () => void;
  getInitials: (email: string) => string;
  getUserDisplayName: () => string;
  getUserDisplayRole: () => string;
  avatarUrl?: string | null;
}

/**
 * Mobile header: logo, phone number (plain link like desktop) below logo, hamburger.
 * Drawer: user section, quick access links, nav links. No call button; phone is in the bar.
 */
export function MobileNav({
  menuOpen,
  setMenuOpen,
  links,
  logoSrc,
  logoAlt,
  phoneNumber,
  location,
  onLoginClick,
  user,
  userRole,
  userProfile,
  hasQuotes,
  hasCarrier,
  onAvatarClick,
  getUserDisplayName,
  getUserDisplayRole,
  avatarUrl = null,
}: MobileNavProps) {
  const { handleMobileNavigation } = useScrollNavigation();
  const pathNoTrailing = location.pathname.replace(/\/$/, '') || '/';
  const hrefNoTrailing = (href: string) => href.replace(/\/$/, '') || '/';
  const telHref = `tel:${phoneNumber.replace(/\s/g, '')}`;
  const phoneAriaLabel = `Call support: ${phoneNumber}`;
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Focus trap when menu is open: move focus to first focusable inside; on close return to hamburger
  useEffect(() => {
    if (!menuOpen) return;
    const panel = menuPanelRef.current;
    const hamburgerButton = hamburgerRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    if (first) {
      first.focus({ preventScroll: true });
    }
    return () => {
      hamburgerButton?.focus({ preventScroll: true });
    };
  }, [menuOpen]);

  // Escape to close menu
  useEffect(() => {
    if (!menuOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen, setMenuOpen]);

  // Keep focus inside drawer when Tab/Shift+Tab
  useEffect(() => {
    if (!menuOpen || !menuPanelRef.current) return;
    const panel = menuPanelRef.current;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = panel.querySelectorAll<HTMLElement>(
      'button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          (last as HTMLElement).focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          (first as HTMLElement).focus();
        }
      }
    };
    panel.addEventListener('keydown', handleKeyDown);
    return () => panel.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, setMenuOpen]);

  return (
    <nav
      className="sticky top-0 z-[51] w-full border-b border-gray-200 bg-gradient-to-r from-[#fbf8f5] to-white shadow-lg lg:hidden"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-between px-4 py-3 gap-2">
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <Brand logoSrc={logoSrc} logoAlt={logoAlt} size="mobile" />
          {phoneNumber && (
            <a
              href={telHref}
              aria-label={phoneAriaLabel}
              className="text-gray-900 text-sm whitespace-nowrap no-underline hover:no-underline hover:text-gray-900 w-fit"
            >
              {phoneNumber}
            </a>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {user && (userRole === 'admin' || userRole === 'agent') && (
            <NotificationBell isMobile className="lg:hidden" />
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-xl bg-white/80 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 hover:border-gray-300 text-gray-700 hover:text-gray-900 shadow-sm hover:shadow-md active:shadow-sm transition-all duration-200 ease-out focus:outline-none"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <Icon
              icon={menuOpen ? 'mdi:close' : 'mdi:menu'}
              className="w-6 h-6 text-inherit"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id={MOBILE_MENU_ID}
            ref={menuPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-b from-gray-50 to-white px-4 pb-6 pt-4 text-gray-900/80 border-t border-gray-100 max-h-[85vh] overflow-y-auto overscroll-contain"
          >
            <ul className="flex flex-col space-y-2">
              {/* User profile section */}
              {user ? (
                <li>
                  <button
                    type="button"
                    className="flex w-full items-center space-x-3 rounded-xl px-4 py-3 bg-white/80 hover:bg-gray-100 border border-gray-900/15 text-left focus:outline-none min-h-[44px] transition-colors"
                    onClick={() => {
                      setMenuOpen(false);
                      setTimeout(onAvatarClick, 300);
                    }}
                    title={user.email}
                  >
                    <Avatar
                      src={avatarUrl || userProfile?.avatar_url || null}
                      name={userProfile?.full_name || getUserDisplayName()}
                      email={user.email}
                      size="md"
                      className="flex-shrink-0 ring-2 ring-white"
                    />
                    <div className="flex flex-col items-start flex-1 min-w-0">
                      <span className="text-sm font-extrabold text-gray-900/80 truncate w-full">
                        {getUserDisplayName()}
                      </span>
                      <span
                        className="text-xs text-gray-900/80 font-semibold truncate w-full"
                        title={getUserDisplayRole()}
                      >
                        {getUserDisplayRole()}
                      </span>
                    </div>
                  </button>
                </li>
              ) : (
                <li>
                  <button
                    type="button"
                    className="w-full rounded-xl bg-primary px-4 py-3 font-semibold text-white min-h-[44px] hover:bg-primary-dark transition-colors focus:outline-none"
                    onClick={() => {
                      setMenuOpen(false);
                      setTimeout(onLoginClick, 300);
                    }}
                  >
                    Login
                  </button>
                </li>
              )}
              {user && (
                <li>
                  <hr className="border-gray-200 my-2" />
                </li>
              )}
              {user && hasQuotes && (
                <li>
                  <Link
                    to="/my-quotes"
                    onClick={() => setTimeout(() => setMenuOpen(false), 300)}
                    className="flex items-center gap-3 w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                  >
                    <Icon icon="mdi:file-document-outline" className="w-5 h-5 flex-shrink-0 text-current" aria-hidden="true" />
                    <span>My Quotes</span>
                  </Link>
                </li>
              )}
              {user && hasCarrier && (
                <li>
                  <Link
                    to="/carrier-portal"
                    onClick={() => setTimeout(() => setMenuOpen(false), 300)}
                    className="flex items-center gap-3 w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                  >
                    <Icon icon="mdi:truck-fast" className="w-5 h-5 flex-shrink-0 text-current" aria-hidden="true" />
                    <span>Carrier Portal</span>
                  </Link>
                </li>
              )}
              {user && userRole === 'admin' && (
                <>
                  <li>
                    <Link
                      to="/admin/quotes"
                      onClick={() => setTimeout(() => setMenuOpen(false), 300)}
                      className="flex items-center gap-3 w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                    >
                      <Icon icon="mdi:format-list-bulleted" className="w-5 h-5 flex-shrink-0 text-current" aria-hidden="true" />
                      <span>Admin Panel</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/analytics"
                      onClick={() => setTimeout(() => setMenuOpen(false), 300)}
                      className="flex items-center gap-3 w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                    >
                      <Icon icon="mdi:chart-line" className="w-5 h-5 flex-shrink-0 text-current" aria-hidden="true" />
                      <span>Analytics</span>
                    </Link>
                  </li>
                </>
              )}
              {user && (hasQuotes || hasCarrier || userRole === 'admin') && (
                <li>
                  <hr className="border-gray-200 my-2" />
                </li>
              )}
              {links.map(({ href, label, isSection, icon }) => {
                const isActiveSection = isSection && pathNoTrailing === '/' && location.hash === `#${href}`;
                const isActiveLink = !isSection && pathNoTrailing === hrefNoTrailing(href);
                const isActive = isActiveSection || isActiveLink;
                return (
                  <li key={href}>
                    {isSection ? (
                      <button
                        type="button"
                        onClick={() => handleMobileNavigation(href, () => setMenuOpen(false))}
                        className="flex items-center gap-3 w-full cursor-pointer rounded-lg px-4 py-3 text-left font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {icon && (
                          <Icon
                            icon={icon}
                            className="w-5 h-5 flex-shrink-0 text-current"
                            aria-hidden="true"
                          />
                        )}
                        <span>{label}</span>
                      </button>
                    ) : (
                      <Link
                        to={href}
                        onClick={() => setTimeout(() => setMenuOpen(false), 300)}
                        className="flex items-center gap-3 w-full cursor-pointer rounded-lg px-4 py-3 text-left font-medium text-gray-900/80 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-100 min-h-[44px] items-center focus:outline-none"
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {icon && (
                          <Icon
                            icon={icon}
                            className="w-5 h-5 flex-shrink-0 text-current"
                            aria-hidden="true"
                          />
                        )}
                        <span>{label}</span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
